import { addDays, format } from 'date-fns';
import { useDateNow, useReduxState } from 'hooks';
import { getOpeningHoursStatus } from 'shared-helpers/courses';
import { dateFnsLocales, getMessageWithPlaceholders } from 'shared-locales';
import { DisplayFormattedDate } from 'components/common';
import { CardExtension } from 'components/common/tasks';

import type { Course } from 'shared-types';

interface Props {
  endDate: string | null;
  openingHours?: Course['openingHours'];
  startDate: string | null;
}

const CoursesTilePublishStatus = ({
  endDate,
  openingHours,
  startDate
}: Props) => {
  const { dateNow } = useDateNow();

  const {
    language,
    messages,
    serverConfig: { timeZone },
    theme
  } = useReduxState();

  const openingHoursStatus = getOpeningHoursStatus({ openingHours, timeZone });

  const showPublishStatus =
    Date.parse(startDate) > dateNow ||
    Date.parse(endDate) >= dateNow ||
    Date.parse(endDate) < dateNow;

  const showOpeningHours = !showPublishStatus && openingHours?.isEnabled;

  if (!showPublishStatus && !showOpeningHours) return null;

  return showPublishStatus ? (
    <CardExtension icon={['far', 'calendar-alt']}>
      <div className="content">
        {Date.parse(startDate) > dateNow ? (
          <div className="date">
            <span className="date-label">{messages.courseOpens}</span>
            <DisplayFormattedDate date={startDate} />
            <span className="date-time">
              <DisplayFormattedDate date={startDate} dateFormat="HH:mm" />
            </span>
          </div>
        ) : Date.parse(endDate) >= dateNow ? (
          <div className="date date-warning">
            <span className="date-label">{messages.courseEnds}</span>
            <DisplayFormattedDate date={endDate} />
            <span className="date-time">
              <DisplayFormattedDate date={endDate} dateFormat="HH:mm" />
            </span>
          </div>
        ) : Date.parse(endDate) < dateNow ? (
          <div className="date date-danger">
            <span className="date-label">{messages.courseHasEnded}</span>
            <DisplayFormattedDate date={endDate} />
          </div>
        ) : null}

        <style jsx>{`
          .date {
            color: ${theme.textColorLight};
          }
          .date-label {
            margin-right: 5px;
          }
          .date-time {
            margin-left: 5px;
          }
          .date-warning {
            color: ${theme.warningStandaloneColor};
          }
          .date-danger {
            color: ${theme.dangerColor};
          }
        `}</style>
      </div>
    </CardExtension>
  ) : showOpeningHours ? (
    <CardExtension
      icon={['far', 'clock']}
      innerStyle={
        openingHoursStatus?.isCurrentlyOpen ? {} : { color: theme.dangerColor }
      }
    >
      {openingHoursStatus?.isCurrentlyOpen
        ? `${messages.courseOpenToday} ${openingHoursStatus.currentOrNextStartTime} - ${openingHoursStatus.currentOrNextEndTime}`
        : openingHoursStatus?.daysUntilNextOpen === 0
        ? getMessageWithPlaceholders({
            message: messages.courseOpensToday,
            placeholders: { time: openingHoursStatus?.currentOrNextStartTime }
          })
        : openingHoursStatus?.daysUntilNextOpen === 1
        ? getMessageWithPlaceholders({
            message: messages.courseOpensTomorrow,
            placeholders: { time: openingHoursStatus?.currentOrNextStartTime }
          })
        : getMessageWithPlaceholders({
            message: messages.courseOpensOnDay,
            placeholders: {
              day: format(
                addDays(new Date(), openingHoursStatus?.daysUntilNextOpen || 0),
                'eeee',
                { locale: dateFnsLocales[language] }
              ),
              time: openingHoursStatus?.currentOrNextStartTime
            }
          })}
    </CardExtension>
  ) : null;
};

export default CoursesTilePublishStatus;
